import React, { useEffect, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DraftDialog } from "@/pages/Inbox";
import ContextTimeline from "@/components/ContextTimeline";
import ContextComposer from "@/components/ContextComposer";
import BidContextCard from "@/components/BidContextCard";
import ProjectMemoryCard from "@/components/ProjectMemoryCard";
import { EngagementStateChip } from "@/components/EngagementChip";
import { fmtDate } from "@/lib/format";
import { Sparkles, Reply, RefreshCw, ArrowRight, Mail, Inbox as InboxIcon, BellRing, Clock, FolderSymlink, MinusCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { useSyncedRefresh } from "@/hooks/useLiveSync";

// Per-project unified context: an iMessage-style conversation flow + AI deal brief.
export default function ContextTab({ bidId, autoReply }) {
  const [ctx, setCtx] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [draftEv, setDraftEv] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [moveOpen, setMoveOpen] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [projects, setProjects] = useState([]);
  const [moveThread, setMoveThread] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [queryDraft, setQueryDraft] = useState("");

  const load = () => api.get(`/bids/${bidId}/context`).then((r) => setCtx(r.data)).catch(() => setCtx(null));
  const loadUnassigned = () => api.get(`/bids/${bidId}/unassigned-threads`).then((r) => setUnassigned(r.data || [])).catch(() => setUnassigned([]));
  const loadProjects = () => api.get(`/bids/${bidId}/client-projects`).then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
  const syncEmail = async () => {
    setSyncing(true);
    try {
      await api.post(`/bids/${bidId}/email/sync`);
      toast.info("Scanning your inbox for this project…");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/bids/${bidId}/email/sync-status`);
          if (!data.running) {
            const n = data.last?.scanned ?? 0, m = data.last?.logged ?? 0;
            toast.success(`Scan complete — ${n} message${n === 1 ? "" : "s"} checked, ${m} newly linked`);
            load(); loadUnassigned(); loadBrief(); setSyncing(false);
            return;
          }
        } catch { /* keep polling */ }
        if (tries < 30) setTimeout(poll, 1500);
        else { load(); loadUnassigned(); loadBrief(); setSyncing(false); }
      };
      setTimeout(poll, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Connect Gmail in the Inbox first"); setSyncing(false); }
  };
  const loadBrief = () => {
    setBriefLoading(true);
    api.get(`/bids/${bidId}/context/brief`).then((r) => setBrief(r.data)).catch(() => setBrief(null)).finally(() => setBriefLoading(false));
  };
  useEffect(() => {
    load(); loadUnassigned(); loadProjects();
    api.get("/email/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [bidId]);
  useEffect(() => { if (ctx && (ctx.counts.emails || ctx.counts.activities)) loadBrief(); /* eslint-disable-next-line */ }, [bidId, ctx?.counts?.emails]);
  useEffect(() => { if (ctx?.bid) setQueryDraft(ctx.bid.email_query_override || ctx.bid.email_query || ""); }, [ctx?.bid?.email_query, ctx?.bid?.email_query_override]);
  const saveQuery = async () => {
    try {
      await api.post(`/bids/${bidId}/email/query`, { query: queryDraft });
      toast.success("Search updated — re-scanning this project's mail");
      setTimeout(() => { load(); loadUnassigned(); loadBrief(); }, 4000);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const resetQuery = async () => {
    try {
      await api.post(`/bids/${bidId}/email/query`, { query: "" });
      toast.success("Reverted to the automatic search");
      setTimeout(() => { load(); }, 3000);
    } catch { toast.error("Could not reset"); }
  };
  useSyncedRefresh(() => { load(); loadUnassigned(); });

  const rs = ctx?.reply_state;
  // A real "you owe a reply" is only when the CLIENT sent last and it needs us (waiting_on "us").
  // When WE sent last but the thread has gone quiet past cadence, that's a proactive check-in
  // nudge — NOT a reply we owe — so it must read differently (this killed the false "You owe a
  // reply" on threads where our message was the last word).
  const trueOwe = !!(rs?.owe && rs?.waiting_on !== "client");
  const checkin = !!(rs?.owe && rs?.waiting_on === "client");
  const owe = trueOwe;
  const eng = ctx?.engagement;
  const replyLabel = trueOwe ? "You owe a reply" : checkin ? "Time for a check-in" : (rs?.last_outbound_at ? "Waiting on client" : "No email yet");
  const otherProjects = projects.filter((p) => p.bid_id !== bidId);
  const lastInbound = (ctx?.items || []).filter((i) => i.type === "email" && i.direction === "in").sort((a, b) => ((a.date || "") < (b.date || "") ? 1 : -1))[0];
  const replyTarget = rs?.owed_event || (lastInbound ? { id: lastInbound.id, counterparty: lastInbound.counterparty, subject: lastInbound.subject } : null);

  const replyToLast = () => {
    if (rs?.owed_event) setDraftEv({ id: rs.owed_event.id, counterparty: rs.owed_event.counterparty, subject: rs.owed_event.subject });
  };
  const toggleTeam = async (it) => {
    try {
      await api.post(`/email/events/${it.id}/team`, { team: !it.team });
      toast.success(it.team ? "Unshared from team" : "Shared with your team");
      load();
    } catch { toast.error("Could not update sharing"); }
  };
  const assignThread = async (threadId, targetBidId, { key, closeMove } = {}) => {
    setAssigning(key || threadId);
    try {
      await api.post(`/email/threads/${threadId}/assign`, { bid_id: targetBidId });
      toast.success(targetBidId === bidId ? "Thread moved into this project" : targetBidId ? "Thread re-routed to another project" : "Removed from this project");
      if (closeMove) setMoveThread(null);
      await Promise.all([load(), loadUnassigned()]);
      loadBrief();
    } catch { toast.error("Could not move thread"); }
    finally { setAssigning(null); }
  };

  // Arriving from a Today nudge (?reply=1): auto-open the reply draft once the owed email is known.
  const autoFired = useRef(false);
  useEffect(() => {
    if (autoReply && !autoFired.current && rs?.owed_event) {
      autoFired.current = true;
      setDraftEv({ id: rs.owed_event.id, counterparty: rs.owed_event.counterparty, subject: rs.owed_event.subject });
    }
  }, [autoReply, rs?.owed_event]);

  return (
    <div className="max-w-[1800px] w-full mx-auto px-4 py-6 space-y-5" data-testid="context-tab">
      {/* AI Deal Brief & Action Header */}
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${trueOwe ? "bg-[#F4C400]/20 text-[#8a6d00]" : checkin ? "bg-amber-50 text-amber-700" : rs?.last_outbound_at ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-500"}`} data-testid="ctx-reply-state">
                {trueOwe ? <BellRing className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />} {replyLabel}
              </span>
              {eng && ["quiet", "chasing", "cold"].includes(eng.state) && (
                <EngagementStateChip state={eng.state} silenceDays={eng.silence_days} followups={eng.unanswered_followups} testid="ctx-engagement-state" />
              )}
              {rs?.reason && <span className="text-[11px] text-slate-400">{rs.reason}</span>}
            </div>
            <div className="mt-3" style={{ display: window.__gfConnectorMode ? "none" : undefined }} data-testid="ctx-deal-brief">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-[#003E87]" /> Deal brief</div>
              {briefLoading ? (
                <div className="text-sm text-slate-400 mt-1">Reading the thread…</div>
              ) : brief?.state ? (
                <div className="mt-1" data-testid="ctx-brief">
                  <p className="text-sm text-slate-700 leading-relaxed">{brief.state}</p>
                  {brief.next_step && (
                    <p className="text-sm text-slate-800 mt-1.5 flex items-start gap-1.5">
                      <ArrowRight className="w-4 h-4 text-[#003E87] mt-0.5 shrink-0" /> {brief.next_step}
                    </p>
                  )}
                </div>
              ) : <div className="text-sm text-slate-400 mt-1">No summary yet — sync some email or log an activity.</div>}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button onClick={replyToLast} disabled={!rs?.owed_event} className="bg-[#003E87] hover:bg-[#00306a] text-white" data-testid="ctx-reply-last">
              <Reply className="w-4 h-4 mr-1.5" /> Reply to last
            </Button>
            <Button variant="outline" size="sm" onClick={loadBrief} data-testid="ctx-refresh-brief"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh brief</Button>
            <Button variant="outline" size="sm" onClick={syncEmail} disabled={syncing} data-testid="ctx-sync-email"><Mail className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-pulse" : ""}`} /> {syncing ? "Scanning…" : "Sync project email"}</Button>
          </div>
        </div>
        <div className="flex gap-4 text-[11px] text-slate-400 mt-4 border-t border-slate-100 pt-3">
          <span data-testid="ctx-count-emails">{ctx?.counts?.emails || 0} emails</span>
          <span>{ctx?.counts?.activities || 0} activities</span>
          <span>{ctx?.counts?.comments || 0} comments</span>
        </div>
        <details className="mt-3 border-t border-slate-100 pt-3" data-testid="ctx-query-editor">
          <summary className="text-[11px] uppercase tracking-wider text-slate-400 cursor-pointer flex items-center gap-1.5 select-none"><Search className="w-3.5 h-3.5" /> Email search terms {ctx?.bid?.email_query_override ? "(custom)" : "(auto)"}</summary>
          <div className="mt-2 space-y-2">
            <p className="text-[11px] text-slate-500">The Gmail search that pulls mail into this project. Tune it if the project name is generic or threads get missed. Gmail syntax works (quotes, OR, from:, to:).</p>
            <Input value={queryDraft} onChange={(e) => setQueryDraft(e.target.value)} className="h-8 text-xs font-mono" data-testid="ctx-query-input" placeholder={'e.g. "Auburn Solar" OR PR-2026-0007'} />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveQuery} data-testid="ctx-query-save" className="bg-[#003E87] hover:bg-[#00306a] text-white">Save &amp; re-scan</Button>
              {ctx?.bid?.email_query_override && <Button size="sm" variant="ghost" onClick={resetQuery} data-testid="ctx-query-reset">Reset to auto</Button>}
            </div>
          </div>
        </details>
      </Card>

      {/* Unassigned threads affordance */}
      {unassigned.length > 0 && (
        <div data-testid="ctx-unassigned-threads" className="flex items-center gap-3 rounded-xl border border-[#F4C400]/40 bg-[#F4C400]/10 px-4 py-3">
          <InboxIcon className="w-4 h-4 text-[#8a6d00] shrink-0" />
          <div className="flex-1 text-sm text-slate-700 min-w-0">
            <span className="font-semibold">{unassigned.length}</span> client thread{unassigned.length === 1 ? "" : "s"} not tied to any project yet.
          </div>
          <Button size="sm" className="bg-[#003E87] hover:bg-[#00306a] text-white shrink-0" data-testid="ctx-move-project-btn" onClick={() => setMoveOpen(true)}>
            Move to project <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      )}

      <ProjectMemoryCard bidId={bidId} readOnly={!!ctx?.bid?.read_only} />

      <BidContextCard bid={ctx?.bid} />

      <ContextTimeline items={ctx?.items || []} onToggleTeam={toggleTeam} onMove={(it) => setMoveThread(it)} />

      <ContextComposer bidId={bidId} canSend={!!status?.can_send} replyTarget={replyTarget}
        onPosted={() => { load(); loadBrief(); }}
        onReconnect={() => { window.location.href = `${API_BASE}/oauth/gmail/login`; }} />

      {/* Pull-in-from-unassigned dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent data-testid="ctx-move-dialog">
          <DialogHeader>
            <DialogTitle>Move threads into this project</DialogTitle>
            <DialogDescription>These emails are on this client but not linked to any project yet. Pull the ones that belong here.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[52vh] overflow-auto -mx-1 px-1">
            {unassigned.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Nothing left to move — every thread is routed.</div>}
            {unassigned.map((t) => (
              <div key={t.thread_id} data-testid={`ctx-unassigned-${t.thread_id}`} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
                <div className={`mt-0.5 shrink-0 ${t.direction === "in" ? "text-emerald-600" : "text-slate-400"}`}><Mail className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{t.subject || t.summary || "(no subject)"}</div>
                  {t.summary && t.subject && <div className="text-[11px] text-slate-500 truncate">{t.summary}</div>}
                  <div className="text-[11px] text-slate-400 truncate">{t.counterparty}{t.last_date ? ` · ${fmtDate(t.last_date)}` : ""}{t.count > 1 ? ` · ${t.count} messages` : ""}</div>
                </div>
                <Button size="sm" variant="outline" disabled={assigning === t.thread_id} onClick={() => assignThread(t.thread_id, bidId)} data-testid={`ctx-pull-${t.thread_id}`} className="shrink-0">
                  {assigning === t.thread_id ? "Moving…" : "Pull in"}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Re-route a bound thread to another project (or unassign it) */}
      <Dialog open={!!moveThread} onOpenChange={(o) => { if (!o) setMoveThread(null); }}>
        <DialogContent data-testid="ctx-thread-move-dialog">
          <DialogHeader>
            <DialogTitle>Move this conversation</DialogTitle>
            <DialogDescription className="truncate">
              “{moveThread?.subject || moveThread?.summary || "this thread"}” — send it to the project it really belongs to, or take it off this one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[52vh] overflow-auto -mx-1 px-1">
            {otherProjects.length === 0 && (
              <div className="text-sm text-slate-400 py-4 text-center">No other projects for this client to move it to.</div>
            )}
            {otherProjects.map((p) => (
              <button key={p.bid_id} data-testid={`ctx-move-target-${p.bid_id}`} disabled={!!assigning}
                onClick={() => moveThread && assignThread(moveThread.thread_id, p.bid_id, { key: p.bid_id, closeMove: true })}
                className="w-full flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-left hover:border-[#003E87]/40 hover:bg-slate-50 disabled:opacity-50">
                <FolderSymlink className="w-4 h-4 text-[#003E87] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{p.name}</div>
                  <div className="text-[11px] text-slate-400">{p.status}{p.capacity_mw ? ` · ${p.capacity_mw} MW` : ""}{p.state ? ` · ${p.state}` : ""}</div>
                </div>
                <span className="text-[11px] text-[#003E87] font-medium shrink-0">{assigning === p.bid_id ? "Moving…" : "Move here"}</span>
              </button>
            ))}
            <button data-testid="ctx-move-unassign" disabled={!!assigning}
              onClick={() => moveThread && assignThread(moveThread.thread_id, null, { key: "unassign", closeMove: true })}
              className="w-full flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-left hover:border-rose-300 hover:bg-rose-50/40 disabled:opacity-50">
              <MinusCircle className="w-4 h-4 text-rose-500 shrink-0" />
              <div className="flex-1 text-sm text-slate-700">Remove from this project <span className="text-slate-400">(back to Unassigned)</span></div>
              <span className="text-[11px] text-rose-500 font-medium shrink-0">{assigning === "unassign" ? "Removing…" : "Remove"}</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <DraftDialog ev={draftEv} open={!!draftEv} onOpenChange={(o) => { if (!o) setDraftEv(null); }}
        canSend={!!status?.can_send} onReconnect={() => { window.location.href = `${API_BASE}/oauth/gmail/login`; }}
        onSent={() => { setDraftEv(null); load(); }} />
    </div>
  );
}
